/**
 * useLossReasonsPage - Hook para orquestrar a página de Motivos de Perda
 * Separa lógica de estado e handlers do componente de apresentação.
 */
import { useState, useCallback, useMemo } from 'react';
import { useLossReasons } from './useLossReasons';
import { LossReason } from '../../../../types';
import { notify } from '../../../../utils/toast';
import { TrashIcon } from '../../../../components/icons';
import { createImportAction, createExportAction } from '../../../../components/shared/ActionMenu';
import { BulkAction } from '../../../../components/shared/BulkActionsBar';

export const useLossReasonsPage = () => {
    const {
        reasons,
        loading,
        createReason,
        updateReason,
        deleteReason,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch
    } = useLossReasons();

    const [searchTerm, setSearchTerm] = useState('');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [reasonToEdit, setReasonToEdit] = useState<LossReason | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [reasonToDelete, setReasonToDelete] = useState<LossReason | undefined>(undefined);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    const filteredReasons = useMemo(() =>
        reasons.filter(reason =>
            reason.name.toLowerCase().includes(searchTerm.toLowerCase())
        ), [reasons, searchTerm]);

    const handleOpenCreateModal = useCallback(() => {
        setReasonToEdit(undefined);
        setIsFormModalOpen(true);
    }, []);

    const handleOpenEditModal = useCallback((reason: LossReason) => {
        setReasonToEdit(reason);
        setIsFormModalOpen(true);
    }, []);

    const handleOpenDeleteModal = useCallback((reason: LossReason) => {
        setReasonToDelete(reason);
        setIsDeleteModalOpen(true);
    }, []);

    const handleSaveReason = useCallback(async (data: Omit<LossReason, 'id' | 'createdAt'>) => {
        try {
            if (reasonToEdit) {
                await updateReason(reasonToEdit.id, data);
                notify.success('Motivo atualizado com sucesso!');
            } else {
                await createReason(data);
                notify.success('Motivo criado com sucesso!');
            }
            setIsFormModalOpen(false);
        } catch (error) {
            notify.error('Erro ao salvar motivo');
        }
    }, [reasonToEdit, updateReason, createReason]);

    const handleConfirmDelete = useCallback(async () => {
        if (reasonToDelete) {
            try {
                await deleteReason(reasonToDelete.id);
                notify.success('Motivo excluído com sucesso!');
                setIsDeleteModalOpen(false);
                setReasonToDelete(undefined);
            } catch (error) {
                notify.error('Erro ao excluir motivo');
            }
        }
    }, [reasonToDelete, deleteReason]);

    const handleBulkDelete = useCallback(async () => {
        const ids = Array.from(selectedIds);
        try {
            await deleteBulk(ids);
            notify.success(`${ids.length} motivo(s) excluído(s) com sucesso!`);
            setIsBulkDeleteModalOpen(false);
        } catch {
            notify.error('Erro ao excluir motivos');
        }
    }, [selectedIds, deleteBulk]);

    const handleDuplicate = useCallback(async (reason: LossReason) => {
        try {
            const { id, createdAt, ...data } = reason;
            await createReason({ ...data, name: `${data.name} (Cópia)` });
            notify.success('Motivo duplicado com sucesso!');
        } catch (error) {
            notify.error('Erro ao duplicar motivo');
        }
    }, [createReason]);

    const handleRefresh = useCallback(async () => {
        if (refetch) {
            await refetch();
            notify.success('Dados atualizados!');
        }
    }, [refetch]);

    const headerActions = useMemo(() => [
        createImportAction(() => notify.info('Funcionalidade de importação em desenvolvimento')),
        createExportAction(() => notify.info('Funcionalidade de exportação em desenvolvimento')),
    ], []);

    const bulkActions: BulkAction[] = useMemo(() => [
        {
            label: 'Excluir',
            icon: TrashIcon,
            onClick: () => setIsBulkDeleteModalOpen(true),
            variant: 'danger' as const
        }
    ], []);

    return {
        filteredReasons,
        loading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        reasonToEdit,
        reasonToDelete,
        isFormModalOpen,
        setIsFormModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveReason,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        handleRefresh,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        headerActions,
        bulkActions
    };
};
